/**
 * Post-add-to-cart upsell popup (product page).
 *
 * <post-atc-upsell> is rendered by snippets/post-add-to-cart-upsell.liquid. It is hidden by default and
 * opens once per session right after the main product form adds an item to the cart, unless the popup
 * product (or one of its colour siblings) is already in the cart. When it opens, the cart drawer that
 * theme.js would normally open on `ajaxProduct:added` is suppressed for that one event.
 *
 * Everything generic (data JSON, colours, sizes, helper, add to cart) lives in the shared
 * assets/post-atc-upsell-core.js (window.PostAtcUpsellCore); this file only adds the product-page specifics:
 * session rule, `ajaxProduct:added` capture listener, cart drawer suppression, Go to Cart, cart id tracking
 * and the confirmation title.
 *
 * Data comes from <script type="application/json" data-post-atc-data> inside the element.
 */
(function () {
  'use strict';

  var selectors = {
    modal: '.post-atc-upsell__modal',
    close: '[data-post-atc-close]',
    goToCart: '[data-post-atc-go-to-cart]',
    addedTitle: '[data-post-atc-added-title]'
  };

  var FOCUS_NAMESPACE = 'post_atc_upsell_focus';
  var SCROLL_NAMESPACE = '.post-atc-upsell';

  /*
    Cart drawer suppression.
    theme.CartDrawer listens to `ajaxProduct:added` and calls `this.open()` (looked up on the prototype at
    call time). We wrap the prototype's `open` once: while the one-shot flag is set, the next call clears the
    flag and does nothing. The `cart:open` listener is bound at drawer init (`this.open.bind(this)`), so it
    holds either the original or the wrapper depending on timing; both open the drawer when the flag is
    clear, which is always the case when the Go to Cart button dispatches `cart:open`.
  */
  var suppressNextDrawerOpen = false;
  var drawerPatched = false;

  function patchCartDrawer() {
    if (drawerPatched) return true;
    if (!window.theme || !theme.CartDrawer || !theme.CartDrawer.prototype) return false;

    var originalOpen = theme.CartDrawer.prototype.open;
    theme.CartDrawer.prototype.open = function () {
      if (suppressNextDrawerOpen) {
        suppressNextDrawerOpen = false;
        return;
      }
      return originalOpen.apply(this, arguments);
    };

    drawerPatched = true;
    return true;
  }

  // theme.js is deferred too; if it has not run yet, retry once the DOM is parsed.
  if (!patchCartDrawer()) {
    document.addEventListener('DOMContentLoaded', patchCartDrawer);
    window.addEventListener('load', patchCartDrawer);
  }

  function defineElement() {
    if (customElements.get('post-atc-upsell')) return;
    if (!window.PostAtcUpsellCore) {
      console.warn('post-atc-upsell: core script (post-atc-upsell-core.js) is missing');
      return;
    }

    class PostAtcUpsell extends window.PostAtcUpsellCore {
      constructor() {
        super();

        this.cartProductIds = new Set();
        this.popupProductIds = new Set();

        this._onProductAdded = this._onProductAdded.bind(this);
        this._onCartUpdated = this._onCartUpdated.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
      }

      connectedCallback() {
        if (!this.bindBody(this)) return;

        this.modal = this.querySelector(selectors.modal);
        this.addedTitleEl = this.querySelector(selectors.addedTitle);

        // Ids that count as "the popup product": upsell product + every colour sibling in the Style select
        this.popupProductIds.add(String(this.product.id));
        this.querySelectorAll('option[data-product-id]').forEach((option) => {
          this.popupProductIds.add(String(option.dataset.productId));
        });
        (this.data.cartProductIds || []).forEach((id) => this.cartProductIds.add(String(id)));

        // Capture phase: runs before CartDrawer's bubble-phase listener so the suppress flag is set in time
        document.addEventListener('ajaxProduct:added', this._onProductAdded, true);
        document.addEventListener('cart:updated', this._onCartUpdated);

        this.querySelectorAll(selectors.close).forEach((el) => el.addEventListener('click', () => this.close()));
        this.querySelectorAll(selectors.goToCart).forEach((el) => {
          el.addEventListener('click', (event) => this._onGoToCart(event));
        });
      }

      disconnectedCallback() {
        document.removeEventListener('ajaxProduct:added', this._onProductAdded, true);
        document.removeEventListener('cart:updated', this._onCartUpdated);
        document.removeEventListener('keydown', this._onKeydown);
      }

      /*============================================================================
        Show / hide
      ==============================================================================*/

      _onProductAdded(event) {
        // The theme also dispatches a detail-less `ajaxProduct:added` on document for failed adds
        var added = event.detail && event.detail.product;
        if (!added || !(event.target instanceof Element)) return;

        // Only the main product form of this page (not quick shop / other forms)
        if (String(added.product_id) !== String(this.data.pageProductId)) return;

        // Whatever was just added is now in the cart
        this.cartProductIds.add(String(added.product_id));

        if (theme.settings.cartType === 'page') return;
        if (this._wasShownThisSession()) return;
        if (this._cartHasPopupProduct()) return;

        // Skip the drawer for this event only; cleared by the wrapper, or on the next tick as a safety net
        suppressNextDrawerOpen = true;
        setTimeout(() => { suppressNextDrawerOpen = false; }, 0);

        this._markShown();
        if (this.addedTitleEl && added.product_title) {
          this.addedTitleEl.textContent = added.product_title;
        }

        // Pre-select the upsell size closest to the size that was just added
        this.parentItem = added;
        this.targetSize = this._addedSize(added);
        if (this.targetSize) {
          this._rebuildOptions(true);
          this._renderHelper();
        }

        // Pre-select the colour closest to the added product's colour (async: loads that colour's product,
        // then keeps the size picked above if it is in stock)
        this._preselectColor(added.product_title);

        this.open();
      }

      _cartHasPopupProduct() {
        for (var id of this.cartProductIds) {
          if (this.popupProductIds.has(id)) return true;
        }
        return false;
      }

      _wasShownThisSession() {
        try {
          return sessionStorage.getItem(this.data.sessionKey) === '1';
        } catch (error) {
          return false;
        }
      }

      _markShown() {
        try {
          sessionStorage.setItem(this.data.sessionKey, '1');
        } catch (error) {
          // Storage unavailable (private mode): the popup may show again, which is acceptable
        }
      }

      // Focus trap, scroll lock and Escape while the popup is open (hooks of the core open()/close())
      _activate() {
        theme.a11y.lockMobileScrolling(SCROLL_NAMESPACE);
        document.documentElement.classList.add('modal-open');
        theme.a11y.trapFocus({
          container: this.modal,
          elementToFocus: this.modal,
          namespace: FOCUS_NAMESPACE
        });
        document.addEventListener('keydown', this._onKeydown);
      }

      _deactivate() {
        theme.a11y.removeTrapFocus({
          container: this.modal,
          namespace: FOCUS_NAMESPACE
        });
        theme.a11y.unlockMobileScrolling(SCROLL_NAMESPACE);
        document.documentElement.classList.remove('modal-open');
        document.removeEventListener('keydown', this._onKeydown);
      }

      _onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      _onGoToCart(event) {
        // Cart page mode: let the link navigate normally
        if (theme.settings.cartType === 'page') return;

        event.preventDefault();
        // theme.Drawers.open() binds a window click listener that closes the drawer on outside clicks; without
        // this the same click bubbles to window and closes the drawer right after `cart:open` (theme.js does the same)
        event.stopPropagation();
        this.close(false);

        suppressNextDrawerOpen = false;
        document.dispatchEvent(new CustomEvent('cart:open'));
      }

      /*============================================================================
        Cart state
      ==============================================================================*/

      _onCartUpdated(event) {
        var cart = event.detail && event.detail.cart;
        if (cart && cart.items) {
          this._setCartProductIds(cart.items);
          return;
        }

        // No cart in the event: refresh from the cart endpoint
        fetch(theme.routes.cart, { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((freshCart) => this._setCartProductIds(freshCart.items || []))
          .catch(() => {});
      }

      _setCartProductIds(items) {
        this.cartProductIds = new Set(items.map((item) => String(item.product_id)));
      }

      /*============================================================================
        Add to cart (core flow + product-page specifics)
      ==============================================================================*/

      _onItemAdded(productId) {
        this.cartProductIds.add(String(productId));
      }

      _afterAdd(cart) {
        // Refreshes the cart count and shipping bar, then re-renders the drawer; closes the popup
        super._afterAdd(cart);

        if (theme.settings.cartType === 'page') {
          window.location = theme.routes.cartPage;
        } else {
          suppressNextDrawerOpen = false;
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      }
    }

    customElements.define('post-atc-upsell', PostAtcUpsell);
  }

  // Core is deferred before this script; fall back to DOMContentLoaded if the order was ever different
  if (window.PostAtcUpsellCore) {
    defineElement();
  } else {
    document.addEventListener('DOMContentLoaded', defineElement);
  }
})();
